import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight,
  Bot,
  Check,
  Clock,
  FileText,
  Globe,
  MessageSquare,
  Send,
  Shield,
  Star,
  TrendingUp,
  Users,
  Zap,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import Footer from '@/components/Footer';
import SiteNavBar from '@/components/SiteNavBar';

// Animation component (matches FlyAgent style)
const RevealOnScroll = ({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setTimeout(() => setIsVisible(true), delay * 100); },
      { threshold: 0.1, rootMargin: '50px' }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [delay]);

  return (
    <div ref={ref} className={`transition-all duration-700 ease-out ${className}`}
      style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(24px)' }}>
      {children}
    </div>
  );
};

// Positions come from the index, not Math.random(), so the page the server
// renders and the page the browser hydrates draw the same field.
const ParticleField = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none motion-reduce:hidden">
    {[...Array(30)].map((_, i) => (
      <div
        key={i}
        className="absolute w-1 h-1 rounded-full bg-violet-500/30"
        style={{
          left: `${(i * 37) % 100}%`,
          top: `${(i * 67) % 100}%`,
          animation: `float ${3 + (i % 5)}s ease-in-out infinite`,
          animationDelay: `${(i % 7) * 0.3}s`,
        }}
      />
    ))}
    <style>{`
      @keyframes float {
        0%, 100% { transform: translateY(0) translateX(0); opacity: 0.3; }
        50% { transform: translateY(-20px) translateX(10px); opacity: 0.8; }
      }
    `}</style>
  </div>
);

const CONTACT_EMAILS = ['manu.mani@zoomlocal.in', 'anand.mahajan@zoomlocal.in', 'kedar.deshmukh@zoomlocal.in'];

const AgencyContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    businessName: '',
    locations: '1-10',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-managed-inquiry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          ...formData,
          to: CONTACT_EMAILS,
        }),
      });

      if (!response.ok) throw new Error('Failed to send');
      setIsSubmitted(true);
    } catch (err: any) {
      console.error('[AgenciesPage] Contact form error:', err);
      setError("Your message didn't send. Try again, or reach us on WhatsApp at +91 84467 12833.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 rounded-full bg-violet-500/20 border border-violet-500/30 flex items-center justify-center mx-auto mb-6">
          <Check className="w-8 h-8 text-violet-400" />
        </div>
        <h3 className="text-2xl font-bold text-white mb-3">Thanks, we have your message</h3>
        <p className="text-gray-400 max-w-md mx-auto">
          Our team will review your inquiry and get back to you within 24 hours to discuss the best setup for your agency.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <label htmlFor="agency-name" className="block text-sm font-medium text-gray-300 mb-1.5">Your name *</label>
          <input
            id="agency-name"
            type="text"
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full bg-gray-800/80 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-500/40 transition-colors"
            placeholder="Your name"
          />
        </div>
        <div>
          <label htmlFor="agency-email" className="block text-sm font-medium text-gray-300 mb-1.5">Email *</label>
          <input
            id="agency-email"
            type="email"
            required
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="w-full bg-gray-800/80 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-500/40 transition-colors"
            placeholder="you@agency.com"
          />
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <label htmlFor="agency-phone" className="block text-sm font-medium text-gray-300 mb-1.5">Phone</label>
          <input
            id="agency-phone"
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            className="w-full bg-gray-800/80 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-500/40 transition-colors"
            placeholder="+91 98765 43210"
          />
        </div>
        <div>
          <label htmlFor="agency-business" className="block text-sm font-medium text-gray-300 mb-1.5">Agency or business name *</label>
          <input
            id="agency-business"
            type="text"
            required
            value={formData.businessName}
            onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
            className="w-full bg-gray-800/80 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-500/40 transition-colors"
            placeholder="Your agency name"
          />
        </div>
      </div>
      <div>
        <label htmlFor="agency-locations" className="block text-sm font-medium text-gray-300 mb-1.5">Number of client locations</label>
        <select
          id="agency-locations"
          value={formData.locations}
          onChange={(e) => setFormData({ ...formData, locations: e.target.value })}
          className="w-full bg-gray-800/80 border border-gray-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-violet-500/50 focus:ring-1 focus:ring-violet-500/25 transition-colors"
        >
          <option value="1-10">1 to 10 locations</option>
          <option value="11-50">11 to 50 locations</option>
          <option value="51-100">51 to 100 locations</option>
          <option value="100+">More than 100 locations</option>
        </select>
      </div>
      <div>
        <label htmlFor="agency-message" className="block text-sm font-medium text-gray-300 mb-1.5">Message</label>
        <textarea
          id="agency-message"
          rows={3}
          value={formData.message}
          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
          className="w-full bg-gray-800/80 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-500/40 transition-colors resize-none"
          placeholder="Tell us about your agency, your clients and what you need"
        />
      </div>
      {error && <p role="alert" className="text-red-400 text-sm">{error}</p>}
      <Button
        type="submit"
        size="lg"
        disabled={isSubmitting}
        className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white rounded-xl py-6 text-lg"
      >
        {isSubmitting ? 'Sending...' : 'Send message'}
        <Send className="w-5 h-5 ml-2" />
      </Button>
    </form>
  );
};

declare global {
  interface Window {
    __GEO_COUNTRY__?: string;
  }
}

const AgenciesPage = () => {
  return (
    <>
      <Helmet>
        <title>Fly for Agencies | AI-Powered Local SEO at Scale</title>
        <meta name="description" content="Manage every client's Google Business Profile through AI. 14 specialized agents cover reviews, rankings, content, and reporting inside Claude or ChatGPT." />
        <link rel="canonical" href="https://www.fly-social.com/agencies" />
        <meta property="og:url" content="https://www.fly-social.com/agencies" />
        <meta property="og:title" content="Fly for Agencies | Local SEO at Scale" />
        <meta property="og:description" content="AI agents that handle GBP management for your entire client roster. Save 10+ hours per week." />
      </Helmet>

      <div className="min-h-screen bg-gray-950 text-white">
        <SiteNavBar />

        {/* Hero Section */}
        <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden pt-16">
          <ParticleField />

          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: `
                linear-gradient(rgba(139, 92, 246, 0.1) 1px, transparent 1px),
                linear-gradient(90deg, rgba(139, 92, 246, 0.1) 1px, transparent 1px)
              `,
              backgroundSize: '64px 64px',
            }}
          />

          <div className="absolute inset-0 bg-gradient-to-b from-violet-950/20 via-transparent to-gray-950" />

          <div className="relative z-10 container mx-auto px-4 text-center">
            <RevealOnScroll>
              <h1 className="text-5xl md:text-7xl font-black mb-6 leading-tight">
                <span className="text-violet-300">
                  Scale Local SEO
                </span>
                <br />
                <span className="text-white">With AI Agents</span>
              </h1>

              <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto mb-8 leading-relaxed">
                Two ways for agencies and freelancers to run their clients' Local SEO:
                <span className="text-white font-semibold"> Fly's Web Dashboard</span>, with white-label workspaces for your entire roster, or
                <span className="text-white font-semibold"> Fly Agent</span>, with 14 AI agents that work through natural conversation.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <a href="#contact">
                  <Button size="lg" className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white px-8 py-6 text-lg rounded-xl shadow-lg shadow-black/30">
                    <Send className="w-5 h-5 mr-2" />
                    Get in touch
                  </Button>
                </a>
                <Link to="/fly-agent">
                  <Button size="lg" className="bg-transparent border border-gray-600 hover:bg-gray-800 text-white px-8 py-6 text-lg rounded-xl">
                    <Bot className="w-5 h-5 mr-2" />
                    See all 14 agents
                  </Button>
                </Link>
              </div>

              <p className="text-sm text-gray-400">Web DIY Dashboard plus AI agents via Claude (MCP), ChatGPT (Actions), and Gemini (OpenAPI)</p>
            </RevealOnScroll>
          </div>

        </section>

        {/* Why Agencies Choose Fly */}
        <section className="py-24 relative bg-gradient-to-b from-gray-950 via-gray-900/50 to-gray-950">
          <div className="container mx-auto px-4">
            <RevealOnScroll>
              <div className="text-center mb-16">
                <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">
                  Why agencies choose Fly
                </h2>
                <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                  Stop drowning in client dashboards. Let AI handle the repetitive work while you focus on strategy.
                </p>
              </div>
            </RevealOnScroll>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
              {[
                {
                  icon: Clock,
                  stat: '10+',
                  unit: 'hours a week',
                  label: 'Time saved',
                  description: 'Automate review responses, reporting, and profile monitoring across all clients',
                },
                {
                  icon: Users,
                  stat: '50+',
                  unit: 'clients',
                  label: 'Scale effortlessly',
                  description: 'Manage unlimited client profiles from a single AI conversation',
                },
                {
                  icon: FileText,
                  stat: '1-Click',
                  unit: '',
                  label: 'Branded reports',
                  description: 'Professional PDF reports with your agency branding, generated instantly',
                },
                {
                  icon: Zap,
                  stat: '14',
                  unit: 'agents',
                  label: 'AI at your command',
                  description: 'Specialized agents for every aspect of GBP management',
                },
              ].map((item, i) => (
                <RevealOnScroll key={i} delay={i} className="h-full">
                  <div className="bg-gray-900/80 rounded-2xl border border-gray-800 p-6 h-full">
                    <item.icon className="w-7 h-7 mb-4 text-violet-300" aria-hidden />
                    <div className="flex items-baseline gap-1.5 mb-1">
                      <span className="text-3xl font-black text-white">{item.stat}</span>
                      <span className="text-sm text-gray-400">{item.unit}</span>
                    </div>
                    <p className="text-sm font-semibold text-gray-200 mb-2">{item.label}</p>
                    <p className="text-sm text-gray-400">{item.description}</p>
                  </div>
                </RevealOnScroll>
              ))}
            </div>
          </div>
        </section>

        {/* What You Can Do */}
        <section className="py-24 relative">
          <div className="absolute inset-0 bg-gradient-to-b from-gray-950 via-violet-950/10 to-gray-950" />

          <div className="relative container mx-auto px-4">
            <RevealOnScroll>
              <div className="text-center mb-16">
                <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">
                  One conversation, endless capabilities
                </h2>
                <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                  Ask your AI assistant anything about your clients' Google profiles.
                </p>
              </div>
            </RevealOnScroll>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {[
                {
                  icon: Star,
                  title: 'Review management',
                  description: 'AI-powered responses, auto-responder, sentiment analysis, and review monitoring across all clients.',
                },
                {
                  icon: TrendingUp,
                  title: 'Rank tracking',
                  description: 'Track keyword rankings across Google Maps for every client. Geographic grid analysis and competitor tracking.',
                },
                {
                  icon: Shield,
                  title: 'Profile protection',
                  description: 'Monitor all client profiles for unauthorized changes. Get alerts for category, hours, and contact info edits.',
                },
                {
                  icon: MessageSquare,
                  title: 'Content publishing',
                  description: 'AI generates engaging posts, schedules them, and publishes across client profiles with consistent brand voice.',
                },
                {
                  icon: FileText,
                  title: 'Branded reporting',
                  description: 'One-click professional reports with your agency logo. Monthly summaries, PDF export, and auto-email delivery.',
                },
                {
                  icon: Globe,
                  title: 'Microsite builder',
                  description: 'Create beautiful landing pages and store locators for clients. Custom domains and auto-generated SEO content.',
                },
              ].map((item, index) => (
                <RevealOnScroll key={index} delay={index} className="h-full">
                  <div className="bg-gray-900/80 rounded-2xl border border-gray-800 p-6 h-full">
                    <div className="flex items-center gap-3 mb-2">
                      <item.icon className="w-5 h-5 shrink-0 text-violet-300" aria-hidden />
                      <h3 className="text-lg font-bold text-white">{item.title}</h3>
                    </div>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.description}</p>
                  </div>
                </RevealOnScroll>
              ))}
            </div>
          </div>
        </section>

        {/* Works With */}
        <section className="py-24 relative bg-gradient-to-b from-gray-950 via-violet-950/10 to-gray-950">
          <div className="container mx-auto px-4">
            <RevealOnScroll>
              <div className="text-center mb-16">
                <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">Bring your own AI</h2>
                <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                  Fly Agent works inside the AI platforms your team already uses.
                </p>
              </div>
            </RevealOnScroll>

            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {[
                { name: 'Claude', protocol: 'MCP Protocol', color: '#f97316', link: '/fly-agent/guides/claude' },
                { name: 'ChatGPT', protocol: 'GPT Actions', color: '#10b981', link: '/fly-agent/guides/chatgpt' },
                { name: 'Gemini', protocol: 'OpenAPI', color: '#3b82f6', link: '/fly-agent/guides/gemini' },
              ].map((ai, index) => (
                <RevealOnScroll key={index} delay={index}>
                  <Link to={ai.link} className="block h-full group">
                    <div className="h-full bg-gray-900/80 rounded-2xl border border-gray-800 p-8 text-center hover:border-gray-600 transition-colors duration-300">
                      <h3 className="text-2xl font-bold text-white mb-2">{ai.name}</h3>
                      <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium mb-4"
                        style={{ backgroundColor: `${ai.color}15`, color: ai.color, borderColor: `${ai.color}30`, borderWidth: '1px' }}>
                        {ai.protocol}
                      </div>
                      <div className="flex items-center justify-center text-sm font-medium text-gray-200 group-hover:translate-x-1 transition-transform">
                        {ai.name} setup guide <ArrowRight className="w-4 h-4 ml-2" />
                      </div>
                    </div>
                  </Link>
                </RevealOnScroll>
              ))}
            </div>
          </div>
        </section>

        {/* What agencies get (no price since 17 Oct 26) */}
        <section className="py-24 relative">
          <div className="container mx-auto px-4">
            <RevealOnScroll>
              <div className="max-w-3xl mx-auto text-center">
                <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">What agencies get</h2>
                <p className="text-gray-400 text-lg mb-12">Full access to all 14 AI agents. Volume discounts available.</p>

                <div className="relative inline-block">
                  <div className="relative bg-gray-900 rounded-3xl border-2 border-violet-500/50 p-8 md:p-12">
                    <p className="text-gray-400 mb-8">Billed monthly. Cancel anytime.</p>

                    <ul className="space-y-4 text-left max-w-sm mx-auto mb-8">
                      {[
                        'All 14 AI agents included',
                        'Works with Claude, ChatGPT and Gemini',
                        'Unlimited API calls',
                        'White-label branded reports',
                        'Multi-workspace management',
                        'Priority support',
                      ].map((item, i) => (
                        <li key={i} className="flex items-center gap-3 text-gray-300">
                          <Check className="w-5 h-5 text-violet-400 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>

                    <a href="#contact">
                      <Button size="lg" className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white px-8 py-6 text-lg rounded-xl w-full">
                        Get in touch
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </Button>
                    </a>
                  </div>
                </div>
              </div>
            </RevealOnScroll>
          </div>
        </section>

        {/* Contact / CTA */}
        <section id="contact" className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-violet-950/50 via-fuchsia-950/50 to-cyan-950/50" />

          <div className="relative container mx-auto px-4">
            <div className="max-w-2xl mx-auto">
              <RevealOnScroll>
                <div className="text-center mb-10">
                  <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">
                    Ready to scale your agency?
                  </h2>
                  <p className="text-lg text-gray-400">
                    Tell us about your agency and we'll help you pick the right setup: Web Dashboard, AI agents, or both.
                  </p>
                </div>

                <div className="bg-gray-900/80 rounded-3xl border border-gray-800 p-8 md:p-10">
                  <AgencyContactForm />
                </div>

                {/* WhatsApp Alternative */}
                <div className="mt-8 text-center">
                  <p className="text-gray-400 text-sm mb-4">Or reach us directly on WhatsApp</p>
                  <a
                    href="https://wa.me/918446712833?text=Hi%2C%20I%27m%20a%20marketing%20agency%20interested%20in%20Fly%20for%20managing%20my%20clients%27%20Local%20SEO"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 px-6 py-4 rounded-2xl bg-gray-900/80 border border-gray-800 hover:border-violet-500/50 transition-colors duration-300 group"
                  >
                    <div className="w-10 h-10 rounded-full bg-violet-500/20 flex items-center justify-center">
                      <MessageSquare className="w-5 h-5 text-violet-400" />
                    </div>
                    <div className="text-left">
                      <p className="text-white font-semibold text-sm group-hover:text-violet-400 transition-colors">Chat on WhatsApp</p>
                      <p className="text-gray-400 text-xs">+91 84467 12833</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-gray-600 group-hover:text-violet-400 group-hover:translate-x-1 transition-all" />
                  </a>
                </div>
              </RevealOnScroll>
            </div>
          </div>
        </section>

        <Footer variant="dark" />
      </div>
    </>
  );
};

export default AgenciesPage;
